import { call, put, takeEvery } from 'redux-saga/effects'
import {
  FetchDocusignSigningEnvelopeOptions,
  DocusignCompleteSigning,
  DocusignDeclineSigning,
  DocusignStartSigning,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import {
  DocusignSigningEnvelopeOptionsResponse,
  DocusignSigningField,
  DocusignSigningFieldRequestItem,
  DocusignStartSigningResponse,
} from 'src/interfaces/docusign'
import {
  processRequestDocusignSigningFields,
  processResponseDocusignSigningFields,
} from 'src/utils/docusign'
import { AsyncAction } from 'src/utils/reduxUtils'
import { ApiService, callClientSecureApi } from './api/callApi'
import { captureSentryError } from 'src/utils/sentry'

interface DocusignStartSigningRequest {
  signingFields?: Array<DocusignSigningFieldRequestItem>
  hiddenFields: Array<DocusignSigningField>
  stepId: string
  embedded: boolean
}

function* onDocusignStartSigning(
  action: AsyncAction<DocusignStartSigningRequest, DocusignStartSigningResponse>,
) {
  try {
    const { signingFields, hiddenFields, stepId, embedded } = action.payload || {}

    const body: Omit<DocusignStartSigningRequest, 'stepId' | 'embedded' | 'hiddenFields' | 'flow'> =
      {}

    if (signingFields?.length) {
      body.signingFields = processRequestDocusignSigningFields(signingFields, hiddenFields)
    }

    const { payload }: { payload: DocusignStartSigningResponse } = yield call(
      callClientSecureApi,
      Endpoints.DocusignStartSigning,
      {
        method: 'POST',
        apiService: ApiService.Paraworks,
        body,
        query: {
          stepId,
          embedded: embedded.toString(),
        },
      },
    )

    const { signingFields: responseSigningFields, hiddenFields: responseHiddenFields } =
      processResponseDocusignSigningFields(payload.signingFields)

    const processedPayload: DocusignStartSigningResponse = {
      ...payload,
      signingFields: responseSigningFields,
      hiddenFields: responseHiddenFields,
    }

    yield put(DocusignStartSigning.success(processedPayload))

    if (action.next) {
      action.next(null, processedPayload)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.docusign.onDocusignStartSigning] error',
    })

    const errorMessage = err?.payload?.message || 'Failed to fetch docusign signing url'

    yield put(DocusignStartSigning.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface DocusignCompleteSigningRequest {
  stepId: string
}

interface DocusignCompleteSigningResponse {
  success: boolean
}

function* onDocusignCompleteSigning(
  action: AsyncAction<DocusignCompleteSigningRequest, DocusignCompleteSigningResponse>,
) {
  try {
    const { stepId } = action.payload

    const { payload }: { payload: DocusignCompleteSigningResponse } = yield call(
      callClientSecureApi,
      Endpoints.DocusignCompleteSigning,
      {
        method: 'POST',
        apiService: ApiService.Paraworks,
        query: {
          stepId,
        },
      },
    )

    yield put(DocusignCompleteSigning.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.docusign.onDocusignCompleteSigning] error',
    })

    const errorMessage = err?.payload?.message || 'Failed to complete signing'

    yield put(DocusignCompleteSigning.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface DocusignDeclineSigningRequest {
  stepId: string
}

function* onDocusignDeclineSigning(action: AsyncAction<DocusignDeclineSigningRequest>) {
  try {
    const { stepId } = action.payload

    yield call(callClientSecureApi, Endpoints.DocusignDeclineSigning, {
      method: 'POST',
      apiService: ApiService.Paraworks,
      query: {
        stepId,
      },
    })

    yield put(DocusignDeclineSigning.success())

    if (action.next) {
      action.next(null)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.docusign.onDocusignDeclineSigning] error',
    })

    const errorMessage = err?.payload?.message || 'Failed to decline signing'

    yield put(DocusignDeclineSigning.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

interface FetchSigningEnvelopeOptionsRequest {
  stepId: string
}

function* onFetchSigningEnvelopeOptions(
  action: AsyncAction<FetchSigningEnvelopeOptionsRequest, DocusignSigningEnvelopeOptionsResponse>,
) {
  try {
    const { stepId } = action.payload || {}

    const { payload }: { payload: DocusignSigningEnvelopeOptionsResponse } = yield call(
      callClientSecureApi,
      Endpoints.FetchDocusignSigningEnvelopeOptions,
      {
        method: 'GET',
        apiService: ApiService.Paraworks,
        query: {
          stepId,
        },
      },
    )

    yield put(FetchDocusignSigningEnvelopeOptions.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (err: any) {
    captureSentryError(err, {
      message: '[saga.docusign.onFetchSigningEnvelopeOptions] error',
    })

    const errorMessage =
      err?.payload?.message || 'Failed to fetch docusign signing envelope options'

    yield put(FetchDocusignSigningEnvelopeOptions.failed(err))

    if (action.next) {
      action.next(errorMessage)
    }
  }
}

export function* docusignSaga() {
  yield takeEvery(DocusignStartSigning.type.REQUEST, onDocusignStartSigning)
  yield takeEvery(DocusignCompleteSigning.type.REQUEST, onDocusignCompleteSigning)
  yield takeEvery(DocusignDeclineSigning.type.REQUEST, onDocusignDeclineSigning)
  yield takeEvery(FetchDocusignSigningEnvelopeOptions.type.REQUEST, onFetchSigningEnvelopeOptions)
}
